/**
 * PoliceDepartmentFilters - Clean filter interface with search and chips
 */

import { Search, X } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { US_STATES } from '../utils';

interface PoliceDepartmentFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  stateFilter: string;
  onStateFilterChange: (value: string) => void;
}

export const PoliceDepartmentFilters = ({
  searchQuery,
  onSearchChange,
  stateFilter,
  onStateFilterChange,
}: PoliceDepartmentFiltersProps) => {
  const hasActiveFilters = searchQuery || stateFilter !== 'all';

  const handleClearAll = () => {
    onSearchChange('');
    onStateFilterChange('all');
  };

  const getStateName = (code: string) => {
    return US_STATES.find((s) => s.code === code)?.name || code;
  };

  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const handleClearSearch = React.useCallback(() => {
    onSearchChange('');
    searchInputRef.current?.focus();
  }, [onSearchChange]);

  return (
    <div className="px-6 py-3 border-b border-gray-200 bg-white">
      {/* Search and Filter Controls */}
      <div className="flex items-center gap-3">
        {/* Search bar */}
        <div className="relative flex-1 max-w-lg">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by department name, state, or aliases..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="block w-full pl-12 pr-12 py-2.5 text-base text-gray-900 placeholder-gray-400 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Select value={stateFilter} onValueChange={onStateFilterChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All States" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All States</SelectItem>
            {US_STATES.map((state) => (
              <SelectItem key={state.code} value={state.code}>
                {state.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button plain onClick={handleClearAll}>
            Clear All
          </Button>
        )}
      </div>

      {/* Active Filter Chips */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 mt-3">
          {searchQuery && (
            <Badge color="zinc" className="inline-flex items-center gap-1.5">
              Search: {searchQuery}
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="hover:text-gray-700"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {stateFilter !== 'all' && (
            <Badge color="blue" className="inline-flex items-center gap-1.5">
              State: {getStateName(stateFilter)}
              <button
                type="button"
                onClick={() => onStateFilterChange('all')}
                className="hover:text-gray-700"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};
